import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  Stack,
  alpha,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { nanoid } from 'nanoid';
import IngestionRuleRow from './IngestionRuleRow';
import ImportRuleLibraryDialog from './ImportRuleLibraryDialog';

// Helper functions to convert between dict and array formats
const rulestoArray = (rules) => {
  if (!rules) return [];

  if (Array.isArray(rules)) {
    // Legacy array format - convert to array with order indices
    return rules.map((rule, index) => ({ ...rule, order: index }));
  }

  if (typeof rules === 'object') {
    // Dict format - sort by keys and add order
    const sortedKeys = Object.keys(rules).sort((a, b) => parseInt(a) - parseInt(b));
    return sortedKeys.map((key, index) => ({
      ...rules[key],
      order: index
    })).filter(rule => rule !== null && rule !== undefined);
  }

  return [];
};

const rulesToDict = (rulesArray) => {
  if (!Array.isArray(rulesArray)) return {};

  const dict = {};
  rulesArray.forEach((rule, index) => {
    // order is the rule's place in this list, and _flatIndex its place on screen
    // (computeGroups); the saved key is the order, and neither field is saved.
    const { order, _flatIndex, ...savedRule } = rule;
    dict[index.toString()] = savedRule;
  });
  return dict;
};

const CUSTOM_GROUP_KEY = '__custom__';

/**
 * Organize a flat rules array into ordered groups.
 * Each group is either "custom" (no _library_id) or a library group.
 * Groups are ordered by the position of their first rule in the flat array.
 */
function computeGroups(rulesArray) {
  const groupMap = new Map();

  rulesArray.forEach((rule, flatIndex) => {
    const groupKey = rule._library_id || CUSTOM_GROUP_KEY;
    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, {
        key: groupKey,
        libraryId: rule._library_id || null,
        libraryName: rule._library_name || null,
        libraryVersion: rule._library_version || null,
        rules: [],
      });
    }
    groupMap.get(groupKey).rules.push({ ...rule, _flatIndex: flatIndex });
  });

  const groups = [...groupMap.values()];
  groups.sort((a, b) => a.rules[0]._flatIndex - b.rules[0]._flatIndex);
  return groups;
}

/**
 * Flatten groups back into a flat rules array (stripping _flatIndex).
 */
function flattenGroups(groups) {
  return groups.flatMap(g => g.rules.map(({ _flatIndex, ...rule }) => rule));
}

/**
 * Find which group key a given flat index belongs to.
 */
function findGroupKeyForIndex(groups, flatIndex) {
  for (const group of groups) {
    if (group.rules.some(r => r._flatIndex === flatIndex)) {
      return group.key;
    }
  }
  return null;
}

const IngestionRuleBuilder = ({ rules, tableSchema, onRulesChange }) => {
  const rulesArray = rulestoArray(rules);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const groups = useMemo(() => computeGroups(rulesArray), [rulesArray]);

  // --- Rule CRUD ---

  const handleAddRule = (level = 'row') => {
    const newRule = {
      id: nanoid(),
      name: '',
      enabled: true,
      level: level,
      condition_logic: 'AND',
      conditions: level === 'row' ? [
        {
          id: nanoid(),
          column: '',
          operator: 'is_empty',
          value: null
        }
      ] : [],
      target_column: level === 'cell' ? '' : null,
      cell_condition: level === 'cell' ? {
        type: 'always',
        value: null,
        values: [],
        negate: false
      } : null,
      action: {
        type: 'skip_row',
        column: null,
        value: null
      }
    };
    // Append to the end of the CUSTOM GROUP, not to the end of the flat array.
    // computeGroups displays and numbers custom rules at the position of the first
    // one, so appending to the tail put a new custom rule on screen above imported
    // library rules while it actually ran after them — the numbered execution order
    // shown to the user was simply false, and two order-dependent transformations
    // produced a different value than the display implied.
    const currentGroups = computeGroups(rulesArray);
    const customGroup = currentGroups.find(group => group.key === CUSTOM_GROUP_KEY);
    if (!customGroup) {
      // No custom rules yet: the custom group is created at the tail, so the flat
      // append already agrees with where it will be displayed.
      onRulesChange(rulesToDict([...rulesArray, newRule]));
      return;
    }
    customGroup.rules.push(newRule);
    onRulesChange(rulesToDict(flattenGroups(currentGroups)));
  };

  const handleUpdateRule = (index, updatedRule) => {
    const newRulesArray = [...rulesArray];
    newRulesArray[index] = updatedRule;
    onRulesChange(rulesToDict(newRulesArray));
  };

  const handleDeleteRule = (index) => {
    const newRulesArray = rulesArray.filter((_, idx) => idx !== index);
    onRulesChange(rulesToDict(newRulesArray));
  };

  // --- Individual rule drag-and-drop (within same group only) ---

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);

    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (dragIndex === dropIndex) return;

    // Only allow drops within the same group
    const dragGroup = findGroupKeyForIndex(groups, dragIndex);
    const dropGroup = findGroupKeyForIndex(groups, dropIndex);
    if (dragGroup !== dropGroup) return;

    const newRulesArray = [...rulesArray];
    const [draggedRule] = newRulesArray.splice(dragIndex, 1);
    newRulesArray.splice(dropIndex, 0, draggedRule);

    onRulesChange(rulesToDict(newRulesArray));
  };

  // --- Group reordering ---

  const handleMoveGroupUp = useCallback((groupIndex) => {
    if (groupIndex <= 0) return;
    const currentGroups = computeGroups(rulesArray);
    const temp = currentGroups[groupIndex - 1];
    currentGroups[groupIndex - 1] = currentGroups[groupIndex];
    currentGroups[groupIndex] = temp;
    onRulesChange(rulesToDict(flattenGroups(currentGroups)));
  }, [rulesArray, onRulesChange]);

  const handleMoveGroupDown = useCallback((groupIndex) => {
    const currentGroups = computeGroups(rulesArray);
    if (groupIndex >= currentGroups.length - 1) return;
    const temp = currentGroups[groupIndex + 1];
    currentGroups[groupIndex + 1] = currentGroups[groupIndex];
    currentGroups[groupIndex] = temp;
    onRulesChange(rulesToDict(flattenGroups(currentGroups)));
  }, [rulesArray, onRulesChange]);

  // --- Library rule management ---

  const handleRemoveLibraryRules = useCallback((libraryId) => {
    const filtered = rulesArray.filter(r => r._library_id !== libraryId);
    onRulesChange(rulesToDict(filtered));
  }, [rulesArray, onRulesChange]);

  // A library already in the table cannot be selected again in the dialog, so an import only
  // ever appends. Refreshing a library is "remove all rules from this library", then import.
  const handleImportRules = useCallback((concreteRules) => {
    onRulesChange(rulesToDict([...rulesArray, ...concreteRules]));
  }, [rulesArray, onRulesChange]);

  // --- Compute running rule number across all groups ---

  const flatIndexToRuleNumber = useMemo(() => {
    const map = {};
    let num = 1;
    for (const group of groups) {
      for (const rule of group.rules) {
        map[rule._flatIndex] = num++;
      }
    }
    return map;
  }, [groups]);

  // --- Render ---

  const renderGroupHeader = (group, groupIndex) => {
    const isCustom = group.key === CUSTOM_GROUP_KEY;
    const isFirst = groupIndex === 0;
    const isLast = groupIndex === groups.length - 1;
    const showReorderButtons = groups.length > 1;

    return (
      <Box
        display="flex"
        alignItems="center"
        sx={{
          py: 1,
          px: 1.5,
          mb: 1.5,
          borderRadius: 1,
          backgroundColor: isCustom ? alpha('#4e85eb', 0.06) : alpha('#16a34a', 0.06),
          border: '1px solid',
          borderColor: isCustom ? alpha('#4e85eb', 0.15) : alpha('#16a34a', 0.15),
        }}
      >
        {!isCustom && (
          <LibraryBooksIcon
            sx={{
              fontSize: 18,
              mr: 1,
              color: '#16a34a',
            }}
          />
        )}
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
          {isCustom ? 'Custom Rules' : group.libraryName || group.libraryId}
        </Typography>
        {!isCustom && group.libraryVersion && (
          <Chip
            label={`v${group.libraryVersion}`}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              mr: 1,
              borderColor: alpha('#16a34a', 0.3),
              color: '#16a34a',
            }}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          {group.rules.length} rule{group.rules.length !== 1 ? 's' : ''}
        </Typography>

        {showReorderButtons && (
          <>
            <Tooltip title="Move group up">
              <span>
                <IconButton
                  size="small"
                  disabled={isFirst}
                  onClick={() => handleMoveGroupUp(groupIndex)}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move group down">
              <span>
                <IconButton
                  size="small"
                  disabled={isLast}
                  onClick={() => handleMoveGroupDown(groupIndex)}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}

        {!isCustom && (
          <>
            <Tooltip title="Remove all rules from this library">
              <IconButton
                size="small"
                onClick={() => handleRemoveLibraryRules(group.libraryId)}
                sx={{ color: 'error.main' }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    );
  };

  const renderGroup = (group, groupIndex) => {
    return (
      <Box key={group.key} sx={{ mb: groups.length > 1 ? 3 : 0 }}>
        {/* Only show group headers when there are multiple groups or library rules */}
        {(groups.length > 1 || group.key !== CUSTOM_GROUP_KEY) && renderGroupHeader(group, groupIndex)}

        <Stack spacing={2}>
          {group.rules.map((rule) => {
            const flatIndex = rule._flatIndex;
            return (
              <div
                key={rule.id}
                draggable
                onDragStart={(e) => handleDragStart(e, flatIndex)}
                onDragOver={(e) => handleDragOver(e, flatIndex)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, flatIndex)}
                style={{
                  borderRadius: 8,
                  backgroundColor: dragOverIndex === flatIndex ? alpha('#4e85eb', 0.05) : 'transparent',
                  transition: 'background-color 0.2s ease'
                }}
              >
                <IngestionRuleRow
                  rule={rule}
                  index={flatIndex}
                  tableSchema={tableSchema}
                  onUpdateRule={handleUpdateRule}
                  onDeleteRule={handleDeleteRule}
                  ruleOrder={flatIndexToRuleNumber[flatIndex] || (flatIndex + 1)}
                />
              </div>
            );
          })}
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper
        sx={{
          p: 3,
          backgroundColor: alpha('#4e85eb', 0.02),
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Stack spacing={2} mb={3}>
          <Typography variant="h6" fontWeight={600}>
            Ingestion Rules
          </Typography>

          <Alert
            severity="info"
            icon={<InfoOutlinedIcon />}
            sx={{
              backgroundColor: alpha('#4e85eb', 0.08),
              border: 'none',
              '& .MuiAlert-icon': {
                color: '#4e85eb'
              }
            }}
          >
            <Typography variant="body2">
              Define rules to validate and transform data during file ingestion.
              <strong> Row-level rules</strong> apply conditions across multiple columns and affect entire rows.
              <strong> Cell-level rules</strong> apply to individual cells within a specific column.
              You can also <strong>import pre-built rule libraries</strong> for common validation standards.
            </Typography>
            {/* The three action groups and when a change to each takes effect (Spec 6 section 2.3). */}
            <Box component="ul" sx={{ my: 1, pl: 2.5 }}>
              <Typography component="li" variant="body2">
                <strong>Trim, Remove Spaces, Set Value, Concatenate and Transform</strong> change the
                rows, as a step of cleaning each table of a file.
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Skip Row</strong> leaves rows out, also a step of cleaning each table.
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Fail Row and Warn</strong> are checked against the table when the file is approved.
              </Typography>
            </Box>
            <Typography variant="body2">
              A check (Fail Row, Warn) applies as it stands whenever a file is checked against the table,
              approved files included. A change to a transformation or a skip reaches a file that is
              already cleaned only through Redo this step on the file.
            </Typography>
          </Alert>
        </Stack>

        {rulesArray.length === 0 ? (
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: 'center',
              backgroundColor: alpha('#000', 0.02),
              borderRadius: 1,
              border: '1px dashed',
              borderColor: alpha('#fff', 0.1)
            }}
          >
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No ingestion rules defined
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add rules to automatically skip, validate, or transform data during upload
            </Typography>
            <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleAddRule('row')}
                size="large"
              >
                Create Row Rule
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleAddRule('cell')}
                size="large"
              >
                Create Cell Rule
              </Button>
              <Button
                variant="outlined"
                startIcon={<LibraryBooksIcon />}
                onClick={() => setImportDialogOpen(true)}
                size="large"
                color="success"
              >
                Import from Library
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {groups.map((group, groupIndex) => renderGroup(group, groupIndex))}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleAddRule('row')}
                sx={{
                  borderStyle: 'dashed',
                  px: 3,
                  '&:hover': {
                    borderStyle: 'solid',
                    backgroundColor: alpha('#4e85eb', 0.05)
                  }
                }}
              >
                Add Row Rule
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleAddRule('cell')}
                sx={{
                  borderStyle: 'dashed',
                  px: 3,
                  '&:hover': {
                    borderStyle: 'solid',
                    backgroundColor: alpha('#7c3aed', 0.05)
                  }
                }}
              >
                Add Cell Rule
              </Button>
              <Button
                variant="outlined"
                startIcon={<LibraryBooksIcon />}
                onClick={() => setImportDialogOpen(true)}
                color="success"
                sx={{
                  borderStyle: 'dashed',
                  px: 3,
                  '&:hover': {
                    borderStyle: 'solid',
                    backgroundColor: alpha('#16a34a', 0.05)
                  }
                }}
              >
                Import from Library
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {importDialogOpen && <ImportRuleLibraryDialog
        open
        onClose={() => setImportDialogOpen(false)}
        tableSchema={tableSchema}
        onImportRules={handleImportRules}
        existingRules={rulesArray}
      />}
    </Box>
  );
};

export default IngestionRuleBuilder;
